import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from '@/lib/apiClient'
import { API_ROUTES, PAGINATION } from '@/constants/apiRoutes'
import { RegistedInformationAction, RegistedInformationDetail, RegistedInformationList, RegistedInformationReadDocAction } from '@/types/registedInformation'
import { REGISTED_INFORMATION_STATUS_ALL, normalizeRegistedInformationStatusFilter } from '@/constants/registedInformation'
import type { RegistedInformationStatusFilter } from '@/constants/registedInformation'

export const getRegistedInformationList = createAsyncThunk<RegistedInformationList, { certIds: string[], userIds: string[], status?: RegistedInformationStatusFilter, page: number, pageSize: number }>(API_ROUTES.REGISTED_INFORMATION_GET_LIST, async ({ certIds, userIds, status, page, pageSize }) => {
  const statusFilter = normalizeRegistedInformationStatusFilter(status)
  const res = await apiClient.post<RegistedInformationList>(API_ROUTES.REGISTED_INFORMATION_GET_LIST, {
    cert_ids: certIds,
    user_ids: userIds,
    status: statusFilter === REGISTED_INFORMATION_STATUS_ALL ? undefined : statusFilter,
    page: page,
    page_size: pageSize
  })
  return res
})

export const getRegistedInformationDetail = createAsyncThunk<RegistedInformationDetail, { imageId: number }>(API_ROUTES.REGISTED_INFORMATION_GET_DETAIL, async ({ imageId }) => {
  const res = await apiClient.get<RegistedInformationDetail>(API_ROUTES.REGISTED_INFORMATION_GET_DETAIL, {
    params: { image_id: imageId },
  })
  return res
})
export const deleteRegistedInformations = createAsyncThunk<RegistedInformationAction, { certIds: string[], userIds: string[], imageIds: number[] }>(API_ROUTES.REGISTED_INFORMATION_DELETE, async ({ certIds, userIds, imageIds }) => {
  const res = await apiClient.delete<RegistedInformationAction>(API_ROUTES.REGISTED_INFORMATION_DELETE, {
    cert_ids: certIds,
    user_ids: userIds,
    image_ids: imageIds
  })
  return res
})

export const dowloadAllImages = createAsyncThunk<string, { imageId: number }>(API_ROUTES.REGISTED_INFORMATION_DOWNLOAD_ALL_IMAGES, async ({ imageId }) => {
  const res = await apiClient.get<string>(`${API_ROUTES.REGISTED_INFORMATION_DOWNLOAD_ALL_IMAGES}?image_id=${imageId}`, {
    responseType: 'blob'
  })
  return res
})

export const readDocs = createAsyncThunk<
  RegistedInformationReadDocAction,
  { certFormatFamilyId: string; files: File[] }
>(
  API_ROUTES.REGISTED_INFORMATION_READ_DOCUMENT,
  async ({ certFormatFamilyId, files }) => {
    const formData = new FormData();
    formData.append("cert_format_family_id", certFormatFamilyId);

    // Thêm từng file vào FormData
    files.forEach((file) => {
      formData.append("files", file); // Gửi nhiều file
    });

    const res = await apiClient.post<RegistedInformationReadDocAction>(
      API_ROUTES.REGISTED_INFORMATION_READ_DOCUMENT,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return res;
  }
);

export const createRegistedInformation = createAsyncThunk<
  RegistedInformationAction,
  {
    analysis_results: Record<string, string>;
    cert_format_family_id: string;
    user_id: string;
    masked_image: string;
    sized_image: string;
    original_image: string;
    expiry_date?: string;
  }
>(API_ROUTES.REGISTED_INFORMATION_CREATE, async ({
  analysis_results,
  cert_format_family_id,
  user_id,
  masked_image,
  sized_image,
  original_image,
  expiry_date,
}) => {
  const res = await apiClient.post<RegistedInformationAction>(API_ROUTES.REGISTED_INFORMATION_CREATE, {
    analysis_results,
    cert_format_family_id,
    user_id,
    masked_image,
    sized_image,
    original_image,
    expiry_date,
  });
  return res;
});

export const editRegistedInformation = createAsyncThunk<
  RegistedInformationAction,
  {
    image_id: number;
    items?: Array<{ analysis_key: string; name: string; value: string }>;
    analysis_results: Record<string, string>;
    cert_format_family_id: string;
    user_id: string;
    masked_image: string;
    sized_image: string;
    original_image: string;
    expiry_date?: string;
  }>(API_ROUTES.REGISTED_INFORMATION_EDIT, async ({
    image_id,
    items,
    analysis_results,
    cert_format_family_id,
    user_id,
    masked_image,
    sized_image,
    original_image,
    expiry_date,
  }) => {
    const res = await apiClient.put<RegistedInformationAction>(API_ROUTES.REGISTED_INFORMATION_EDIT, {
      image_id,
      ...(items ? { items } : {}),
      analysis_results,
      cert_format_family_id,
      user_id,
      masked_image,
      sized_image,
      original_image,
      expiry_date,
    });
    return res;
  });

const initialState: RegistedInformationList = {
  items: [],
  analysis_result: null,
  message: "",
  page: PAGINATION.PAGE,
  page_size: PAGINATION.PAGE_SIZE,
  status: "",
  total: 0,
  loading: false,
  error: null,
}

const registedInformationSlice = createSlice({
  name: 'registedInformation',
  initialState,
  reducers: {
    clearAnalysisResult: (state) => {
      state.analysis_result = null
    },
  },
  extraReducers: (builder) => {
    builder
      // GET LIST
      .addCase(getRegistedInformationList.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getRegistedInformationList.fulfilled, (state, action) => {
        state.items = action.payload.items
        state.message = action.payload.message
        state.page = action.payload.page
        state.page_size = action.payload.page_size
        state.status = action.payload.status
        state.total = action.payload.total
        state.loading = false
      })
      // DELETE
      .addCase(deleteRegistedInformations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteRegistedInformations.fulfilled, (state, action) => {
        state.message = action.payload.message
        state.status = action.payload.status
        state.loading = false
      })
      .addCase(deleteRegistedInformations.rejected, (state, action) => {
        state.error = action.error.message || 'Error'
        state.loading = false
      })
      // READ DOCS
      .addCase(readDocs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(readDocs.fulfilled, (state, action) => {
        state.analysis_result = action.payload;
        state.message = action.payload.message;
        state.status = action.payload.status;
        state.loading = false;
      })
      .addCase(readDocs.rejected, (state, action) => {
        state.error = action.error.message || 'Error'
        state.loading = false
      })
      // CREATE
      .addCase(createRegistedInformation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createRegistedInformation.fulfilled, (state, action) => {
        state.message = action.payload.message
        state.status = action.payload.status
        state.loading = false
      })
      .addCase(createRegistedInformation.rejected, (state, action) => {
        state.error = action.error.message || 'Error'
        state.loading = false
      })
      // EDIT
      .addCase(editRegistedInformation.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(editRegistedInformation.fulfilled, (state, action) => {
        state.message = action.payload.message
        state.status = action.payload.status
        state.loading = false
      })
      .addCase(editRegistedInformation.rejected, (state, action) => {
        state.error = action.error.message || 'Error'
        state.loading = false
      })
  },
})

export const { clearAnalysisResult } = registedInformationSlice.actions
export default registedInformationSlice.reducer
